import {Button, Typography, MenuItem} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import Modal from 'Shared/components/Modal';
import {useDropzone} from 'react-dropzone';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import './UploadEnrollmentApplicationModal.sass';
import {
  uploadEnrollmentApplications,
  getAllowedEnrollmentApplicationTemplates,
  clearEnrollmentApplicationTemplates as clearAllowedEnrollmentApplicationTemplates,
  clearAgreementAcknowledgment,
  changeAgreementAccepted,
  EnrollmentApplicationTemplateType,
  changeFileUploadsInProgress,
  changeFileUploadCancelTokens,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import * as O from 'fp-ts/lib/Option';
import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {pipe} from 'fp-ts/lib/pipeable';
import {toEither, isLoaded, Async, Loaded} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {renderAsync} from 'Shared/components/RenderAsync';
import {createSnackbar} from 'Core/ducks/snackbar';
import {EnrollmentApplicationTemplate} from 'Enrollment/models/EnrollmentApplicationTemplate';
import BrokerSpreadsheetAcknowledgementStatement from './BrokerSpreadsheetAcknowledgmentStatement';
import {eq} from 'Shared/utilities/equals';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {Scope} from 'Shared/constants/Scope';
import EnrollmentApplicationTemplateDownloadSection from './EnrollmentApplicationTemplateDownloadSection';
import {prop} from 'Shared/utilities/prop';
import generateRandomNumber from 'Shared/utilities/UniqueRandomNumberGenerator';
import {sequenceS} from 'fp-ts/lib/Apply';
import {FileUploadInProgress} from 'Enrollment/models/FileUploadInProgress';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {CancelTokenSource} from 'axios';
import {FileUploadCancelTokenDetail} from 'Enrollment/models/FileUploadCancelTokenDetail';

const MAX_SIZE = 29360128; // 28 MB

const hasAbilityToUploadFile = (scopes: Scope[]) => scopes.some(scope => /enrolltrack.search.fileupload/.test(scope));

export type FileUploadErrors = {
  selectedFormError: string | boolean;
  fileError: string | boolean;
};

const getFileExtension = (EnrollmentApplicationTemplateTypeId: EnrollmentApplicationTemplateType) => {
  return '.xls,.xlsx';
};

const doesSelectedTemplateRequireAcknowledgement = (
  templates: Async<EnrollmentApplicationTemplate[]>,
  templateId: EnrollmentApplicationTemplateType
) => isLoaded(templates) && (templates.Data?.find(eq('id', templateId))?.acknowledgmentRequired as boolean);

const uploadEnrollmentApplicationCurried = (file: File) => (templateId: number) => (fileInUploadProgressId: string) => (
  cancelTokenSource: CancelTokenSource
) => (key?: string) => (groupId?: string) => (id?: string) =>
  uploadEnrollmentApplications(file, templateId, fileInUploadProgressId, cancelTokenSource, key, groupId, id);

export type EnrollmentApplicationModalProps = {
  onClose: () => void;
  fileId: string | undefined;
  groupId: string | undefined;
};
const UploadEnrollmentApplicationModal = (props: EnrollmentApplicationModalProps) => {
  const {onClose, groupId, fileId} = props;
  const [selectedTemplateId, setSelectedTemplateId] = useState<O.Option<EnrollmentApplicationTemplateType>>(O.none);
  const [file, setFile] = useState<File | undefined>();
  const [errors, setErrors] = useState<FileUploadErrors>({selectedFormError: false, fileError: false});
  const scopes = useSelector(selectUserScopes);
  const {
    allowedEnrollmentApplicationTemplates,
    agreementAcknowledgment,
    agreementAccepted,
    fileUploadsInProgress,
    fileUploadCancelTokens,
  } = useSelector((state: ReducerState) => state.enrollmentFileProcessing);

  const dispatch = useDispatch();

  const canUpload = hasAbilityToUploadFile(scopes);
  var source = axiosInstance.CancelToken.source();

  let fileInUploadProgressId: O.Option<string> = O.none;
  if (isLoaded(fileUploadsInProgress)) {
    const allIds = fileUploadsInProgress.Data.map(prop('id')) as string[];
    fileInUploadProgressId = O.some(generateRandomNumber(allIds).toString());
  }

  useEffect(() => {
    dispatch(getAllowedEnrollmentApplicationTemplates());
    if (groupId || canUpload) {
      setSelectedTemplateId(O.some(EnrollmentApplicationTemplateType.BrokerSpreadsheet));
      setFile(undefined);
      dispatch(changeAgreementAccepted(false));
    }

    return () => {
      dispatch(clearAllowedEnrollmentApplicationTemplates());
      dispatch(clearAgreementAcknowledgment());
    };
  }, [dispatch, groupId, canUpload]);

  const uploadFile = () => {
    const agreementAcceptedDateKey: O.Option<string> =
      isLoaded(agreementAcknowledgment) && agreementAcknowledgment.Data.agreementAcceptedDateKey
        ? O.some(agreementAcknowledgment.Data.agreementAcceptedDateKey)
        : O.none;

    const linkGroupId: O.Option<string> = O.fromNullable(groupId);

    const id: O.Option<string> = O.fromNullable(fileId);
    pipe(
      uploadEnrollmentApplicationCurried,
      O.some,
      O.ap(O.fromNullable(file)),
      O.ap(selectedTemplateId),
      O.ap(fileInUploadProgressId),
      O.ap(O.some(source)),
      O.map(upload => upload(O.toUndefined(agreementAcceptedDateKey))),
      O.map(upload => upload(O.toUndefined(linkGroupId))),
      O.map(upload => upload(O.toUndefined(id))),
      O.map(dispatch)
    );
  };

  const onDropAccepted = (acceptedFiles: File[]) => {
    if (O.isSome(selectedTemplateId)) {
      setFile(acceptedFiles[0]);
    }
    setErrors({
      ...errors,
      fileError: false,
    });
  };
  const onDropRejected = (acceptedFiles: File[]) => {
    setFile(undefined);
    setErrors({
      ...errors,
      fileError:
        acceptedFiles[0].size > MAX_SIZE
          ? `File Exceeds Maximum Allowed Size of ${MAX_SIZE}`
          : 'File Format Not Accepted.',
    });
    acceptedFiles[0].size > MAX_SIZE
      ? dispatch(createSnackbar(`File Exceeds Maximum Allowed Size of ${MAX_SIZE}`))
      : dispatch(createSnackbar('File Format Not Accepted.'));
  };
  const {getInputProps, getRootProps} = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: pipe(
      selectedTemplateId,
      O.map(getFileExtension),
      O.getOrElse(() => '')
    ),
    maxSize: MAX_SIZE,
  });
  const handleUploadEnrollmentFile = () => {
    uploadFile();
    if (isLoaded(fileUploadsInProgress) && O.isSome(fileInUploadProgressId)) {
      const fileUploadCancelToken: FileUploadCancelTokenDetail = {
        fileId: fileInUploadProgressId.value,
        cancelTokenSource: source,
        isCancel: false,
        isProcessingByServer: false,
      };
      dispatch(changeFileUploadCancelTokens([...fileUploadCancelTokens, fileUploadCancelToken]));

      const fileInUploadProgress: FileUploadInProgress = {
        id: fileInUploadProgressId.value,
        fileName: file!.name,
      };
      dispatch(
        changeFileUploadsInProgress(
          new Loaded<FileUploadInProgress[]>([...fileUploadsInProgress.Data, fileInUploadProgress])
        )
      );
    }

    onClose();
  };

  const handleEnrollmentTemplateTypeChange = (value: EnrollmentApplicationTemplateType) => {
    setSelectedTemplateId(O.some(value));
    setFile(undefined);
    dispatch(changeAgreementAccepted(false));
  };
  const disableUploadButton = (): boolean | undefined => {
    let disabled = false;
    if (!O.isSome(selectedTemplateId) || !file) {
      disabled = true;
    }
    if (
      O.isSome(selectedTemplateId) &&
      doesSelectedTemplateRequireAcknowledgement(allowedEnrollmentApplicationTemplates, selectedTemplateId.value) &&
      !agreementAccepted &&
      !isLoaded(agreementAcknowledgment)
    ) {
      disabled = true;
    }
    return disabled;
  };
  return (
    <Modal open className="enrollment-file-upload-modal" onClose={onClose} title="Upload Files" disableBackdropClick>
      {pipe(
        sequenceS(E.either)({
          templates: toEither(allowedEnrollmentApplicationTemplates),
          fileUploadsInProgress: toEither(fileUploadsInProgress),
        }),
        E.fold(renderAsync, ({templates, fileUploadsInProgress}) => {
          if (agreementAccepted && !isLoaded(agreementAcknowledgment)) {
            return renderAsync(agreementAcknowledgment);
          }
          return (
            <>
              <h4>Please select the file format you want to upload.</h4>
              <OutlinedSelect
                id="enrollment-file-upload-modal-options"
                className="enrollment-file-upload-modal__template-Options"
                label="Enrollment File Type"
                onChange={e =>
                  handleEnrollmentTemplateTypeChange(parseInt(e.target.value, 10) as EnrollmentApplicationTemplateType)
                }
                value={O.isSome(selectedTemplateId) ? selectedTemplateId.value : ''}
                disabled={Boolean(groupId) || canUpload}
              >
                {templates.map((option, index) => (
                  <MenuItem key={option.id} value={option.id}>
                    <span id={`enrollment-file-upload-modal-option-${index}`}>{option.name}</span>
                  </MenuItem>
                ))}
              </OutlinedSelect>
              {pipe(
                selectedTemplateId,
                O.fold(
                  () => {},
                  templateId => {
                    const template = templates.find(eq('id', templateId));

                    return <EnrollmentApplicationTemplateDownloadSection template={template!} />;
                  }
                )
              )}
              <div
                {...getRootProps({
                  className:
                    'enrollment-file-upload-modal__file-input' + (O.isNone(selectedTemplateId) ? ' disabled' : ''),
                })}
              >
                <input {...getInputProps()} type="file" multiple hidden disabled={O.isNone(selectedTemplateId)} />
                <Typography
                  className="enrollment-file-upload-modal__content"
                  variant="body2"
                  color={!!errors.fileError ? 'error' : 'initial'}
                >
                  {(file && file.name) ||
                    errors.fileError ||
                    'Click or Drop a file within this container to upload a new file.'}
                </Typography>
              </div>
              {pipe(
                selectedTemplateId,
                O.fold(
                  () => {},
                  templateId =>
                    doesSelectedTemplateRequireAcknowledgement(allowedEnrollmentApplicationTemplates, templateId) && (
                      <BrokerSpreadsheetAcknowledgementStatement />
                    )
                )
              )}
            </>
          );
        })
      )}
      <div className="modal__footer modal__footer-file-upload">
        <Button
          id="enrollment-file-upload-modal-close-button"
          className="modal__footer-fileupload-actions"
          variant="contained"
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          id="enrollment-file-upload-modal-upload-button"
          className="modal__footer-file-upload-actions"
          color="primary"
          variant="contained"
          disabled={disableUploadButton()}
          onClick={handleUploadEnrollmentFile}
        >
          Upload
        </Button>
      </div>
    </Modal>
  );
};

export default UploadEnrollmentApplicationModal;
